import { useEffect, useRef, useState } from "react";
import { Check, Loader2, Mic, Plus, Square, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import useCortiDictation from "../../hooks/useCortiDictation";
import PulmonologySaveButton from "../PulmonologySaveButton/PulmonologySaveButton";
import "./PlanSection.css";

export default function PlanSection({ onContinue, onCancel, sectionsComplete, record, saveRecord, saving, canSave }) {
  const { t, i18n } = useTranslation("pulmonology");
  const [conclusion, setConclusion] = useState("");
  const [savedPlans, setSavedPlans] = useState([]);
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const persistedRecord = Array.isArray(record) ? record[0] : record;
  const nextPlanId = useRef(1);
  const { dictationRef, status: dictationStatus, error: dictationError, toggleDictation } = useCortiDictation({
    language: i18n.resolvedLanguage || i18n.language,
    onTranscript: (text) => setConclusion((current) => `${current}${current && !/\s$/.test(current) ? " " : ""}${text}`),
  });
  const isDictationRecording = dictationStatus === "recording";
  const isDictationConnecting = dictationStatus === "connecting";

  useEffect(() => {
    if (!persistedRecord?.conclusionPlan) return;
    setSavedPlans([{ id: persistedRecord.id, text: persistedRecord.conclusionPlan }]);
    setConclusion("");
  }, [persistedRecord]);

  const persistConclusion = () => {
    const text = conclusion.trim() || savedPlans.at(-1)?.text;
    if (!text) return;
    return saveRecord?.("conclusion-plan", { conclusionPlan: text }, persistedRecord?.id);
  };

  const saveConclusion = () => {
    const text = conclusion.trim();
    if (!text) return;
    if (editingPlanId !== null) {
      setSavedPlans((items) => items.map((item) => item.id === editingPlanId ? { ...item, text } : item));
    } else {
      setSavedPlans((items) => [...items, { id: nextPlanId.current++, text }]);
    }
    setConclusion("");
    setEditingPlanId(null);
  };

  return (
    <div className="pulmo-form">
      <div className="pulmo-clinical-summary"><strong>{t("plan.summaryTitle")}</strong><span>{persistedRecord?.conclusionPlan || t("common.notRecorded")}</span></div>
      <div className="pulmo-plan-editor__heading">
        <label className="pulmo-field-label" htmlFor="pulmo-plan">{t("plan.conclusion")}</label>
        <div className="pulmo-plan-dictation">
          <span>{t("reason.autoDictate")}</span>
          <span className={`pulmo-dictation-switch${isDictationRecording ? " is-on" : ""}${isDictationConnecting ? " is-connecting" : ""}`} aria-hidden="true"><span /></span>
          <button type="button" className={`pulmo-dictation-mic${isDictationRecording ? " is-recording" : ""}${isDictationConnecting ? " is-connecting" : ""}`} aria-label={isDictationRecording || isDictationConnecting ? t("reason.stopDictation") : t("reason.startDictation")} aria-pressed={isDictationRecording} onClick={toggleDictation}>{isDictationConnecting ? <Loader2 size={14} /> : isDictationRecording ? <Square size={12} /> : <Mic size={14} />}</button>
        </div>
      </div>
      <textarea id="pulmo-plan" value={conclusion} onChange={(event) => setConclusion(event.target.value)} onKeyDown={(event) => {
        if (event.key === "Enter" && !event.shiftKey) {
          event.preventDefault();
          saveConclusion();
        }
      }} />
      {dictationError && <span className="pulmo-plan-editor__error" role="status">{dictationError}</span>}
      {savedPlans.length > 0 && <div className="pulmo-plan-records">
        {savedPlans.map((item) => <div className="pulmo-plan-record" key={item.id}>
          <button type="button" className="pulmo-plan-record__text" aria-label={t("plan.editConclusion")} onClick={() => {
            setConclusion(item.text);
            setEditingPlanId(item.id);
          }}>{item.text}</button>
          <div className="pulmo-plan-record__actions">
            <button type="button" className="is-delete" aria-label={t("reason.deleteComplaint")} title={t("reason.deleteComplaint")} onClick={() => {
              setSavedPlans((items) => items.filter((plan) => plan.id !== item.id));
              setEditingPlanId((current) => current === item.id ? null : current);
            }}><X size={15} /></button>
          </div>
        </div>)}
      </div>}
      <div className="pulmo-plan-editor__footer"><button type="button" className="pulmo-plan-editor__add" onClick={saveConclusion}><Plus size={12} /> {t(editingPlanId === null ? "plan.addConclusion" : "plan.updateConclusion")}</button><PulmonologySaveButton onClick={persistConclusion} saving={saving} disabled={!canSave} /></div>
      <div className="pulmo-plan-footer"><button type="button" className="pulmo-outline-button" onClick={() => setCancelConfirmOpen(true)}>{t("common.cancelConsultation")}</button><div><small>{sectionsComplete}</small><button type="button" className="pulmo-primary-button" onClick={onContinue}>{t("plan.continue")}</button></div></div>
      {cancelConfirmOpen && <div className="pulmo-cancel-dialog-backdrop"><div className="pulmo-cancel-dialog" role="dialog" aria-modal="true" aria-labelledby="pulmo-cancel-title"><h3 id="pulmo-cancel-title">{t("plan.cancelTitle")}</h3><p>{t("plan.cancelDescription")}</p><div><button type="button" className="pulmo-outline-button" onClick={() => setCancelConfirmOpen(false)}>{t("plan.keepConsultation")}</button><button type="button" className="pulmo-primary-button" onClick={() => {
        setCancelConfirmOpen(false);
        onCancel?.();
      }}>{t("plan.confirmCancel")}</button></div></div></div>}
      <corti-dictation ref={dictationRef} aria-hidden="true" className="pulmo-hidden-dictation" />
    </div>
  );
}
